import { Dispatch, useEffect, useMemo, useState } from "react";

import { useSocket } from "components";
import { extractSection, insertNewSection } from "components/Documents/Content/use-content-context-hook";
import { DocumentSectionType } from "constants/documents";
import { Channel } from "pusher-js";
import { useDealActions, useDocumentsActions, useUserRoleSelector } from "store";
import { Document, DocumentAnswer, DocumentQuestion, DocumentSection } from "store/types";
import { IFile } from "store/types/deal";

import { RoleName } from "../../constants";

type Channels = { common: Channel; globalDocsFiles: Channel | null };

export const useDocumentSocketHook = ({
  documentId,
  userId,
  dealId,
  setDocument
}: {
  documentId: number;
  userId: number;
  dealId: number | null;
  setDocument: Dispatch<React.SetStateAction<Document<true>>>;
}) => {
  const [channels, setChannels] = useState<Channels | null>(null);
  const role = useUserRoleSelector();
  const isExternalUser = useMemo(() => role?.name === RoleName.Guest || role?.name === RoleName.Viewer, [role]);

  const commonChannelName = `private-document-${documentId}`;
  const globalDocsFilesChannelName = `private-scanning-global-document-${documentId}`; //subscribe to this channel if document is global

  const socket = useSocket();
  const { handleFile } = useDealActions();
  const { getDocumentSectionDataAction } = useDocumentsActions();

  // Subscribe
  useEffect(() => {
    setChannels({
      common: socket.subscribe(commonChannelName),
      globalDocsFiles: !dealId ? socket.subscribe(globalDocsFilesChannelName) : null
    });

    // Unsubscribe
    return () => {
      socket.unsubscribe(commonChannelName);
      if (!dealId) {
        socket.unsubscribe(globalDocsFilesChannelName);
      }
    };
  }, []);

  useEffect(() => {
    const { common: commonChannel } = channels || {};

    if (!commonChannel) return;

    // commonChannel.bind("pusher:subscription_succeeded", () => {
    //   console.log(`[${commonChannel.name}]: connection succeed`);
    // });

    // commonChannel.bind("pusher:subscription_error", (error: any) => {
    //   console.log(`[${commonChannel.name}]: connection error`, error);
    // });

    // commonChannel.bind("error", (error: any) => {
    //   console.log("[Comments error]: ", error);
    // });

    commonChannel.bind("section.created", ({ section, emittedBy }: { section: DocumentSection; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      //hide specific sections for external users related to report
      if (
        isExternalUser &&
        (section.type === DocumentSectionType.Graph ||
          section.type === DocumentSectionType.Table ||
          section.type === DocumentSectionType.KPI ||
          section.text?.includes("inline-measure"))
      )
        return;

      setDocument((prev) => ({
        ...prev,
        sections: insertNewSection(prev.sections, section)
      }));
    });

    commonChannel.bind("section.updated", ({ section, emittedBy }: { section: DocumentSection; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      //hide specific sections for external users related to report
      if (
        isExternalUser &&
        (section.type === DocumentSectionType.Graph ||
          section.type === DocumentSectionType.Table ||
          section.type === DocumentSectionType.KPI ||
          section.text?.includes("inline-measure"))
      ) {
        setDocument((prev) => ({
          ...prev,
          sections: extractSection(prev.sections || [], section.id),
          questions: prev.questions?.filter((item) => item.section_id !== section.id),
          answers: prev.answers?.filter((item) => item.section_id !== section.id),
          ...(prev.files?.length && {
            files: prev.files.filter((item) => item.document_section_id !== section.id)
          })
        }));
        return;
      }

      setDocument((prev) => ({
        ...prev,
        sections: prev.sections.map((item) => (item.id === section.id ? section : item))
      }));
    });

    commonChannel.bind("section.deleted", ({ sectionId, emittedBy }: { sectionId: number; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      setDocument((prev) => ({
        ...prev,
        sections: extractSection(prev.sections || [], sectionId),
        questions: prev.questions?.filter((item) => item.section_id !== sectionId),
        answers: prev.answers?.filter((item) => item.section_id !== sectionId),
        ...(prev.files?.length && {
          files: prev.files.filter((item) => item.document_section_id !== sectionId)
        })
      }));
    });

    commonChannel.bind(
      "sections.reordered",
      ({
        order,
        // documentId,
        emittedBy
      }: {
        emittedBy: number;
        // documentId: number;
        order: Array<{ id: number; position: number }>;
      }) => {
        if (emittedBy === userId) return; // Skip event handler if it is own event
        setDocument((prev) => ({
          ...prev,
          sections: prev.sections
            .map((section) => {
              const updatedSection = order.find((item) => item.id === section.id);
              if (!updatedSection) return section;

              return {
                ...section,
                order: updatedSection.position
              };
            })
            .sort((sec1, sec2) => sec1.order - sec2.order)
        }));
      }
    );

    commonChannel.bind(
      "question.created",
      ({ question, emittedBy }: { question: DocumentQuestion; emittedBy: number }) => {
        if (emittedBy === userId) return; // Skip event handler if it is own event
        setDocument((prev) => ({
          ...prev,
          questions: [...prev.questions, question]
        }));
      }
    );

    commonChannel.bind(
      "question.updated",
      ({ question, emittedBy }: { question: DocumentQuestion; emittedBy: number }) => {
        if (emittedBy === userId) return; // Skip event handler if it is own event
        setDocument((prev) => ({
          ...prev,
          questions: prev.questions.map((item) => (item.id === question.id ? question : item))
        }));
      }
    );

    commonChannel.bind("question.deleted", ({ questionId, emittedBy }: { questionId: number; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      setDocument((prev) => ({
        ...prev,
        questions: prev.questions.filter((item) => item.id !== questionId),
        answers: prev.answers.filter((item) => item.question_id !== questionId)
      }));
    });

    commonChannel.bind("answer.created", ({ answer, emittedBy }: { answer: DocumentAnswer; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      setDocument((prev) => ({
        ...prev,
        answers: [...prev.answers, answer]
      }));
    });

    commonChannel.bind("answer.updated", ({ answer, emittedBy }: { answer: DocumentAnswer; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      setDocument((prev) => ({
        ...prev,
        answers: prev.answers.map((item) => (item.id === answer.id ? answer : item))
      }));
    });

    commonChannel.bind("answer.deleted", ({ answerId, emittedBy }: { answerId: number; emittedBy: number }) => {
      if (emittedBy === userId) return; // Skip event handler if it is own event
      setDocument((prev) => ({
        ...prev,
        answers: prev.answers.filter((item) => item.id !== answerId)
      }));
    });

    commonChannel.bind("AI-answer.published", async ({ documentSectionId }: { documentSectionId: number }) => {
      const { section, answers, questions } = await getDocumentSectionDataAction(documentSectionId);

      //section section what was updated
      //answers answers that were updated from current section
      //questions questions that were updated from current section

      //question ids that answers was updated
      const questionsIds = questions.map((item) => item.id);

      setDocument((prev) => ({
        ...prev,
        sections: prev.sections.map((item) => (item.id === section.id ? section : item)),
        answers: [...prev.answers.filter((item) => !questionsIds.includes(item.question_id)), ...answers].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        ),
        questions: [...prev.questions.filter((item) => !questionsIds.includes(item.id)), ...questions].sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        )
      }));
    });
  }, [channels]);

  // Listen events
  useEffect(() => {
    const { globalDocsFiles: globalDocsFilesChannel } = channels || {};
    if (!globalDocsFilesChannel) return;

    globalDocsFilesChannel.bind("files.checked", ({ file }: { file: IFile }) => {
      if (userId === file?.user_id) {
        handleFile(file, "update");
      }
    });
  }, [channels]);
};
